import { EVALUATION_ANCHORS } from "@lib/constants/scoringScales";
import { Link } from "react-router";

import { GuideArticle, Section } from "../GuideShell";

export function AnchorsPage() {
  return (
    <GuideArticle
      slug="anchors"
      title="Behavioural anchors"
      intro={
        <p>
          An "anchor" in Rarion is what ties a score to evidence. There are two kinds: the named
          labels on the 1–5 evaluation scale (<em>evaluation anchors</em>), and the concrete
          evidence rows that back each score (<em>behavioural anchors</em>). Together they make
          every competency score traceable to specific observed behaviour.
        </p>
      }
    >
      <Section id="what-is-an-anchor" title="What is an anchor?">
        <p>
          A competency score on its own is just a number. An anchor turns it into a defensible
          judgement: a named meaning attached to the score, plus the evidence that justifies it.
        </p>
        <p>
          Rarion requires every competency score to be backed by either inline evidence text or one
          or more behavioural-anchor rows. Unanchored scores are rejected at the model layer — you
          can't save a 4/5 without showing what 4/5 looks like for this candidate on this dimension.
        </p>
      </Section>

      <Section id="evaluation-anchors" title="Evaluation anchors (the 1–5 labels)">
        <p>
          Each score on the 1–5 evaluation scale has a named anchor. These are fixed across the
          system and define what each score <em>means</em> in terms of the bar set by the role
          blueprint.
        </p>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase text-text-faint">
              <th className="py-2 pr-4">Score</th>
              <th className="py-2 pr-4">Short</th>
              <th className="py-2">Anchor</th>
            </tr>
          </thead>
          <tbody>
            {EVALUATION_ANCHORS.map((anchor) => (
              <tr key={anchor.score} className="border-b border-border last:border-b-0">
                <td className="py-1.5 pr-4 align-top font-medium text-text-primary">
                  {anchor.score}
                </td>
                <td className="py-1.5 pr-4 align-top text-text-secondary">{anchor.short}</td>
                <td className="py-1.5 text-text-secondary">{anchor.label}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>
          See{" "}
          <Link to="/help/scoring-scales" className="text-link hover:underline">
            scoring scales
          </Link>{" "}
          for how the 1–5 evaluation relates to the 1–10 required level set on the blueprint.
        </p>
      </Section>

      <Section id="behavioural-anchors" title="Behavioural anchors (the evidence rows)">
        <p>Each behavioural anchor is an evidence record carrying:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Evidence text:</strong> a description of the observed behaviour — at least
            twenty characters, concrete enough that another assessor can interpret it.
          </li>
          <li>
            <strong>Source artefact:</strong> a pointer back to the artefact the evidence came from
            — typically an interview contribution (a note, transcript, or assessment form response),
            but also CV review notes or candidate-supplied write-ups.
          </li>
          <li>
            <strong>Author:</strong> who recorded it.
          </li>
          <li>
            <strong>AI-generated flag:</strong> whether the anchor was drafted by the LLM (during
            interview-round synthesis or the evidence-suggestion tool) or typed by a human.
          </li>
        </ul>
        <p>
          The flag matters: AI-drafted anchors are a starting point for the assessor's judgement,
          not the judgement itself. The recruiter is expected to read, accept, edit, or discard each
          one before saving the assessment.
        </p>
      </Section>

      <Section id="how-anchors-arrive" title="How anchors arrive on an assessment">
        <p>There are three paths:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>The assessment workflow</strong> reads existing interview contributions, runs an
            evidence-extraction pass, and produces a fresh assessment version with behavioural
            anchors already attached.
          </li>
          <li>
            <strong>Interview round synthesis</strong> runs when an interview step is marked
            complete; it gathers per-dimension evidence from the round's contributions, which the
            cross-step assessment workflow then draws on.{" "}
            <Link to="/help/round-synthesis" className="text-link hover:underline">
              Details here.
            </Link>
          </li>
          <li>
            <strong>Manual entry / suggest-evidence:</strong> assessors can type behavioural anchors
            directly, or ask the suggest-evidence tool to draft candidates from the available source
            artefacts.
          </li>
        </ul>
      </Section>

      <Section id="why-evidence-backed" title="Why every score must be evidence-backed">
        <p>
          The unanchored-score path is the path to drift: a panel comes in with numbers but can't
          reconstruct what those numbers meant a week later. Anchored scores keep the assessment as
          a record of <em>what was observed</em>, not just <em>how it felt</em> — which is what
          makes the assessment defensible if the decision is questioned, and re-usable if the
          candidate is reconsidered for an adjacent role.
        </p>
      </Section>
    </GuideArticle>
  );
}
